'use client';

import { useEffect, useRef, useState } from 'react';
import type { backButton as BackButton } from '@telegram-apps/sdk-react';
import { useNavigation } from '../contexts/NavigationContext';

interface BackButtonOptions {
  customHandler?: () => void;
  isDisabled?: boolean;
}

declare global {
  interface Window {
    globalCustomHandler: (() => void) | undefined;
    globalIsDisabled: boolean;
  }
}

type BackButtonModule = typeof BackButton;

/** Lazy import handle — only ever resolved when this hook is mounted,
 * which on web is guarded by `useCapability('telegramBackButton')`. The
 * SDK lands in a separate dynamic chunk so the web bundle stays clean.
 * Resolves once per page load. */
let backButtonPromise: Promise<BackButtonModule | null> | null = null;
function loadBackButton(): Promise<BackButtonModule | null> {
  if (backButtonPromise) return backButtonPromise;
  if (typeof window === 'undefined') {
    backButtonPromise = Promise.resolve(null);
    return backButtonPromise;
  }
  backButtonPromise = import('@telegram-apps/sdk-react').then((m) => m.backButton);
  return backButtonPromise;
}

/**
 * Устанавливает глобальный обработчик backButton.
 * Mount this only inside the TMA shell or behind
 * `useCapability('telegramBackButton')`. The SDK chunk is imported
 * lazily so plain-web bundles aren't affected unless this hook is
 * actually mounted.
 */
export function useTelegramBackButtonSetup() {
  const navigation = useNavigation();
  const navRef = useRef(navigation);
  navRef.current = navigation;
  const [bb, setBb] = useState<BackButtonModule | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadBackButton().then((mod) => {
      if (!cancelled) setBb(mod);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!bb) return;
    if (!bb.isSupported()) return;

    if (!bb.isMounted()) {
      try {
        bb.mount();
      } catch (error) {
        console.error('Failed to mount back button:', error);
        return;
      }
    }

    const handleBackClick = () => {
      if (window.globalIsDisabled) return;
      if (window.globalCustomHandler) {
        window.globalCustomHandler();
        return;
      }
      const nav = navRef.current;
      if (nav.canGoBack()) nav.goBack();
    };

    bb.onClick(handleBackClick);
    return () => bb.offClick(handleBackClick);
  }, [bb]);

  // Visibility tracking — uses navigation.canGoBack() so it works
  // under both URL adapter and legacy stack-nav.
  useEffect(() => {
    if (!bb) return;
    if (!bb.isSupported()) return;

    if (navigation.canGoBack()) {
      bb.show();
    } else {
      bb.hide();
    }
  }, [bb, navigation, navigation.state.stacks, navigation.state.activeTab]);
}

/**
 * Хук для экранов — только обновляет глобальные переменные.
 * НЕ регистрирует обработчики!
 */
export function useTelegramBackButton(options?: BackButtonOptions) {
  const { customHandler, isDisabled = false } = options || {};

  useEffect(() => {
    window.globalCustomHandler = customHandler;
    window.globalIsDisabled = isDisabled;

    return () => {
      window.globalCustomHandler = undefined;
      window.globalIsDisabled = false;
    };
  }, [customHandler, isDisabled]);
}
